import { createContext, useContext, useMemo, type ReactNode } from 'react'
import type { HomepageCarouselDeduplication } from './homepageOfferCarouselDeduplication'

export type HomepageOfferCarouselId =
  | 'heroOfferCarousel'
  | 'luxPlusMonthTakeoverHiddenOfferCarousel'
  | 'localDestinationOfferCarousel'
  | 'maldivesCharterCarousel'
  | 'offerEndingSoonCarousel'
  | 'topPicksForMeCarousel'
  | 'societeStayUpgradeCarousel'
  | 'tourOfferCarousel'
  | 'cruiseOfferCarousel'
  | 'cruiseRoyalCaribbeanCampaignCarousel'
  | 'experienceLuxPlusCarousel'
  | 'smartExperienceOfferCarousel'
  | 'luxPlusOfferCarousel'
  | 'hotelOfferCarousel'
  | 'ultraLuxOfferCarousel'

interface HomepageOfferCarouselScopeValue {
  carouselId: HomepageOfferCarouselId
  deduplication: HomepageCarouselDeduplication
}

const HomepageOfferCarouselScopeContext = createContext<HomepageOfferCarouselScopeValue | undefined>(undefined)

const RESPECT_DEDUPLICATION: HomepageCarouselDeduplication = { mode: 'respect' }

export function useHomepageOfferCarouselScope(): HomepageOfferCarouselScopeValue | undefined {
  return useContext(HomepageOfferCarouselScopeContext)
}

type Props = {
  id: HomepageOfferCarouselId
  children: ReactNode
} & (
  | { mode: 'respect' }
  | { mode: 'allow', reason: string }
)

function HomepageOfferCarousel(props: Props) {
  const { id, children, mode } = props
  const reason = mode === 'allow' ? props.reason : undefined
  const scopeValue = useMemo((): HomepageOfferCarouselScopeValue => ({
    carouselId: id,
    deduplication: mode === 'allow' && reason
      ? { mode: 'allow', reason }
      : RESPECT_DEDUPLICATION,
  }), [id, mode, reason])

  return (
    <HomepageOfferCarouselScopeContext.Provider value={scopeValue}>
      {children}
    </HomepageOfferCarouselScopeContext.Provider>
  )
}

export function HomepageOfferCarouselPassthrough(props: { children: ReactNode }) {
  return (
    <HomepageOfferCarouselScopeContext.Provider value={undefined}>
      {props.children}
    </HomepageOfferCarouselScopeContext.Provider>
  )
}

export default HomepageOfferCarousel
